/**
 * builds the checkbox id for a CollapsibleSection node of an agencies tree
 * @param prefix - namespace for the tree rendering the node, so two trees never collide
 * @param idPath - position path of the node inside the tree (e.g. 'rp0p2')
 * @param agencyId - the agency the node represents
 */
export function makeAgencyNodeId(prefix, idPath, agencyId) {
  const safeId = String(agencyId).replaceAll(/[^a-zA-Z0-9_-]/g, '_');
  return `${prefix}-${idPath}--a${safeId}`;
}

function pushMatch(out, agency, ancestorExpandIds) {
  out.push({
    agencyId: agency.AgencyID,
    expandCheckboxIds: [...ancestorExpandIds]
  });
}

function matchesName(agency, query) {
  return String(agency.AgencyName ?? '')
    .toLowerCase()
    .includes(query);
}

/**
 * DFS preorder list of matches for Cmd+F-style navigation over an agencies tree
 * @param roots - the agencies tree, as stored in agencyManagement.agenciesTree
 * @param query - free text matched against each agency name
 * @param nodeIdPrefix - same prefix the tree passes to makeAgencyNodeId, so the
 *   returned expandCheckboxIds point at the ancestors actually rendered
 */
export function collectAgencyMatchEntries(roots, query, nodeIdPrefix) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery || !Array.isArray(roots)) return [];

  const out = [];

  function walk(agencies, idPath, ancestorExpandIds) {
    if (!Array.isArray(agencies)) return;

    agencies.forEach((agency, index) => {
      const nextPath = `${idPath}p${index}`;
      const hasChildren =
        Array.isArray(agency.Member_Agencies) &&
        agency.Member_Agencies.length > 0;
      const nameMatch = matchesName(agency, normalizedQuery);

      if (!hasChildren) {
        if (nameMatch) pushMatch(out, agency, ancestorExpandIds);
        return;
      }

      if (nameMatch) pushMatch(out, agency, ancestorExpandIds);
      walk(agency.Member_Agencies, nextPath, [
        ...ancestorExpandIds,
        makeAgencyNodeId(nodeIdPrefix, nextPath, agency.AgencyID)
      ]);
    });
  }

  walk(roots, 'r', []);
  return out;
}
